import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import {
	TranslationsEditorFeatureState,
	TranslationsFeatureState,
	ITranslateProvider,
	TranslationsUpdateHandlers,
} from '../types'
import { name as translationFeatureName, TranslateProviderSymbol } from '../symbols'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	AppContextRef,
	BatchingStrategy,
	BatchingStrategySymbol,
	FeatureStateSymbol,
	IRendererPropsExtender,
	IDataRequirementsChecker,
	CarmiInstance,
} from '@wix/thunderbolt-symbols'
import { IFeatureState } from 'thunderbolt-feature-state'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import { getTranslation } from '../translationsUrl'

export const TranslateProvider = withDependencies(
	[named(FeatureStateSymbol, translationFeatureName)],
	(featureState: IFeatureState<TranslationsFeatureState>): ITranslateProvider => {
		return {
			getTranslateFn: async () => {
				const translations = await featureState.get().translations

				return (featureNamespace, key, defaultValue) => translations[featureNamespace]?.[key] || defaultValue
			},
		}
	}
)

export const TranslationsLoader = withDependencies(
	[TranslateProviderSymbol, named(FeatureStateSymbol, translationFeatureName), BatchingStrategySymbol, CarmiInstance],
	(
		{ getTranslateFn }: ITranslateProvider,
		featureState: IFeatureState<TranslationsEditorFeatureState>,
		batchingStrategy: BatchingStrategy,
		{ translations }: DSCarmi
	): IModelUpdatesHandlersImplementor<TranslationsUpdateHandlers> & IDataRequirementsChecker => {
		return {
			featureName: translationFeatureName,
			handlers: {
				loadTranslations: async ({ translationLanguage }) => {
					const translationsPromise = getTranslation(translationLanguage)

					translationsPromise.then((translationsMap) => {
						translations.setGlobalTranslations(translationsMap)
					})

					featureState.update((state) => ({
						...state,
						translations: translationsPromise,
					}))

					const { updateTranslator } = featureState.get()
					if (updateTranslator) {
						const translate = await getTranslateFn()
						batchingStrategy.batch(() => updateTranslator(translate))
					}
				},
			},
			pendingRequirements: () => {
				return featureState.get().translations.then(() => {
					return Promise.resolve()
				})
			},
		}
	}
)

export const TranslationsRendererPropsExtender = withDependencies(
	[TranslateProviderSymbol, named(FeatureStateSymbol, translationFeatureName)],
	(
		{ getTranslateFn }: ITranslateProvider,
		featureState: IFeatureState<TranslationsFeatureState>
	): IRendererPropsExtender => ({
		async extendRendererProps() {
			return {
				translate: await getTranslateFn(),
				contextCallbackRef: (appContextRef: AppContextRef) => {
					featureState.update((state) => ({ ...state, updateTranslator: appContextRef.updateTranslator }))
				},
			}
		},
	})
)
